/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Link as CLink } from 'carbon-components-react';
import cx from 'classnames';
import React, { MouseEventHandler } from 'react';
import { Link as RRLink } from 'react-router-dom';

import { getReactHref } from './util';

// During testing we don't use React routing for `client` tests, but we do it for `shared` tests.
// The difference is that in `shared` we need to wrap our component with `react-router` which
// we do thanks to `renderWithProviders()`.
// For old tests in `client`, enzyme expects the`<Link />` to be an `<a />` link.

const RENDER_RAW_LINKS = process.env.RENDER_RAW_LINKS === 'true';

export interface LinkProps {
  href: string;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  inline?: boolean;
  visited?: boolean;
  target?: string;
  rel?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}

const Link = ({
  children,
  className,
  href,
  disabled,
  inline,
  visited,
  ...other
}: LinkProps) => {
  const reactHref = !disabled && getReactHref(href);
  if (!RENDER_RAW_LINKS && reactHref) {
    return (
      <RRLink
        to={reactHref}
        className={cx({
          'bx--link': true,
          'wa-nxt-link': true,
          'bx--link--inline': inline,
          'bx--link--visited': visited,
          [`${className}`]: className
        })}
        {...other}>
        {children}
      </RRLink>
    );
  } else {
    return (
      <CLink
        href={href}
        className={className}
        disabled={disabled}
        inline={inline}
        visited={visited}
        {...other}>
        {children}
      </CLink>
    );
  }
};

Link.defaultProps = {
  className: '',
  disabled: false,
  inline: false,
  visited: false
};

export default Link;
